import _ from 'lodash'
import dayjs from 'dayjs'
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import {
  E_LEARNING_LEARNER_LOG_RESULT,
  MODULE_TYPE,
} from '../../../../constants/eLearning'
import {
  setAnswered,
  setNextQuestion,
  setPreviousQuestion,
  setSectionResult,
  setSectionTesting,
  resetOnViewState,
  resetAnswer,
} from '../../eTesting'
import {
  setAnswerEEvaluation,
  setNextQuestion as setNextQuestionEva,
  setPreviousQuestion as setPreviousQuestionEva,
  setSectionResult as setSectionResultEva,
  setSectionEvaluate,
  resetAnswer as resetAnswerEva,
} from '../../eEvaluation'
import {
  handleCheckLessonComplete,
  handleEEvaluationProgress,
  handleETestingProgress,
  handleEmbedContentProgress,
  handleResetETestEEva,
  handleUpdateLogLearnerPerLesson,
  handleUploadFileProgress,
  isProgressDiff,
} from './events'
import {
  resetModuleProgress,
  setEnableModule,
  setFileFinished,
  setLessonData,
  setModuleProgress,
  setSelectedLesson,
  setStartModule,
  setSubmitModule,
  setTimeSpentLogUuid,
  setVideoState,
  stopLessonLoading,
  setInitialPreview,
} from '.'
import { getETesting } from 'src/services/eTesting'
import updateTimeSpentlog from 'src/modules/ELearning/Course/Learning/handler/updateTimeSpentLog'

export const listenerMiddlewareELearning = createListenerMiddleware()

// handle Embed Content progress
listenerMiddlewareELearning.startListening({
  actionCreator: setVideoState,
  effect: (action, listenerApi) => {
    const { type, payload } = action
    if (type.includes('setVideoState') && _.isNil(payload.played)) return

    const progress = handleEmbedContentProgress(listenerApi, payload)
    if (isProgressDiff(listenerApi, progress)) {
      listenerApi.dispatch(setModuleProgress(Number(progress)))
    }
  },
})

// handle Upload File progress
listenerMiddlewareELearning.startListening({
  matcher: isAnyOf(setSelectedLesson, setFileFinished),
  effect: (action, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    if (selectedLesson.moduleType !== MODULE_TYPE.UPLOAD_FILE) return

    const progress = handleUploadFileProgress(selectedLesson)
    if (isProgressDiff(listenerApi, progress)) {
      listenerApi.dispatch(setModuleProgress(Number(progress)))
    }
  },
})

// handle E-Testing progress
listenerMiddlewareELearning.startListening({
  matcher: isAnyOf(
    stopLessonLoading,
    setStartModule,
    setAnswered,
    setNextQuestion,
    setPreviousQuestion,
    setSectionResult
  ),
  effect: (action, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    if (selectedLesson.moduleType !== MODULE_TYPE.E_TESTING) return
    if (selectedLesson.isSubmitted && !_.isEmpty(selectedLesson.endTime)) return
    if (!selectedLesson.isInProgress) return

    const eTesting = listenerApi.getState().eTesting
    const progress = handleETestingProgress(eTesting)
    if (isProgressDiff(listenerApi, progress)) {
      listenerApi.dispatch(setModuleProgress(progress))
    }
  },
})

// handle E-Evaluation progress
listenerMiddlewareELearning.startListening({
  matcher: isAnyOf(
    setAnswerEEvaluation,
    setNextQuestionEva,
    setPreviousQuestionEva,
    setSectionResultEva
  ),
  effect: (action, listenerApi) => {
    const { type } = action
    if (type.includes('setSectionResult')) {
      listenerApi.dispatch(setModuleProgress(100))
      return
    }

    const eEvaluation = listenerApi.getState().eEvaluation
    const progress = handleEEvaluationProgress(eEvaluation)
    if (isProgressDiff(listenerApi, progress)) {
      listenerApi.dispatch(setModuleProgress(progress))
    }
  },
})

// handle E-Testing/E-Eva reset when initializing data
listenerMiddlewareELearning.startListening({
  actionCreator: setInitialPreview,
  effect: (action, listenerApi) => {
    const eLearningLearning = listenerApi.getState().eLearningLearning
    const selected = _.get(eLearningLearning, 'selectedLesson', {})
    handleResetETestEEva(listenerApi, selected)
  },
})

// handle E-Testing/E-Eva reset when no submit
listenerMiddlewareELearning.startListening({
  actionCreator: setSelectedLesson,
  effect: (action, listenerApi) => {
    const oldELearningPreview = listenerApi.getOriginalState().eLearningLearning
    const { eLearningLearning } = listenerApi.getState()
    const oldSelected = _.get(oldELearningPreview, 'selectedLesson', {})
    const selected = _.get(eLearningLearning, 'selectedLesson', {})
    const type = _.get(selected, 'type', '')

    handleResetETestEEva(listenerApi, oldSelected)

    if (!selected.isSubmitted) {
      if (type === MODULE_TYPE.E_TESTING) {
        listenerApi.dispatch(setSectionTesting())
        listenerApi.dispatch(resetOnViewState())
      }

      if (type === MODULE_TYPE.E_EVALUATION) {
        listenerApi.dispatch(setSectionEvaluate())
      }
    }
  },
})

// handle time spent log when change module
listenerMiddlewareELearning.startListening({
  actionCreator: setSelectedLesson,
  effect: async (action, listenerApi) => {
    const { __env__: env } = window
    if (env.ENABLE_OIC_EL !== 'true') return

    const oldELearningPreview = listenerApi.getOriginalState().eLearningLearning
    const oldSelected = _.get(oldELearningPreview, 'selectedLesson', {})
    const oldType = _.get(oldSelected, 'moduleType', '')
    const oldUuid = _.get(oldSelected, 'uuid', '')
    const newUuid = _.get(action, 'payload.uuid', '')
    const timeSpentLogUuid = _.get(oldELearningPreview, 'timeSpentLogUuid', '')
    const progress = _.get(oldSelected, 'progress', 0)

    // if selected the same one module, do nothing
    if (newUuid != oldUuid) {
      if (
        [MODULE_TYPE.EMBED_CONTENT, MODULE_TYPE.SCORM].includes(oldType) &&
        timeSpentLogUuid
      ) {
        // update log for old module
        listenerApi.dispatch(updateTimeSpentlog(true, progress))
      }
      listenerApi.dispatch(setTimeSpentLogUuid(''))
    }
  },
})

// handle enable next module
listenerMiddlewareELearning.startListening({
  actionCreator: setModuleProgress,
  effect: async (e, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    const oldSelectedLesson =
      listenerApi.getOriginalState().eLearningLearning.selectedLesson
    const uuid = _.get(selectedLesson, 'uuid', '')
    const oldUuid = _.get(oldSelectedLesson, 'uuid', '')
    const oldPercent = _.get(oldSelectedLesson, 'progress', 0)
    if (uuid === oldUuid && oldPercent === 100) {
      listenerApi.dispatch(setEnableModule())
      return
    }
  },
})

listenerMiddlewareELearning.startListening({
  actionCreator: setStartModule,
  effect: async (action, listenerApi) => {
    const { selectedLesson, eLearningCourseBatchLearner } =
      listenerApi.getState().eLearningLearning
    if (selectedLesson.isSubmitted) return

    const type = _.get(selectedLesson, 'moduleType', '')
    const isETesting = type === MODULE_TYPE.E_TESTING
    const startTime = dayjs().format('YYYY-MM-DD HH:mm:ss').toString()

    // handle update log when start e-test/e-eva
    if (isETesting) {
      const uuid = _.get(selectedLesson, 'eTestingUuid', '')
      const moduleUuid = _.get(selectedLesson, 'uuid', '')
      const eLearningLessonRefModule = _.get(
        selectedLesson,
        'eLearningLessonRefModule',
        ''
      )
      const payload = {
        uuid,
        moduleUuid,
        startTime,
        eLearningCourseBatchLearner,
        eLearningLessonRefModule,
        isModule: true,
      }
      listenerApi.dispatch(resetAnswer())
      await listenerApi.dispatch(getETesting(payload))
    }

    if (type === MODULE_TYPE.E_EVALUATION) {
      listenerApi.dispatch(resetAnswerEva())
    }

    // handle update log when start e-test/e-eva
    const body = {
      logLearnerPerLessonUuid: _.get(selectedLesson, 'logUuid', ''),
      data: {
        percent: 0,
        result: E_LEARNING_LEARNER_LOG_RESULT.INPROGRESS,
        startTime,
        endTime: null,
      },
    }
    if (isETesting) {
      body.data = { ...body.data, score: null, totalScore: null }
    }
    await listenerApi.dispatch(handleUpdateLogLearnerPerLesson(body))
  },
})

// handle e-testing UI state after score is fetched — the log-per-lesson record
// itself is now written server-side during submit, atomically with the answer
// save (see e-learning.service.ts ELearningService.createAnswer, the handler
// behind POST /e-learning/e-testing/answer/{eTestingUuid}): if that write
// fails, the whole submit request fails and rolls back, so there's no partial
// state left for the client to patch up here. This listener is UI-state only.
listenerMiddlewareELearning.startListening({
  type: 'eTesting/getETestingScore/fulfilled',
  effect: async ({ payload }, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    if (_.isEmpty(selectedLesson)) return

    const oldResult = _.get(selectedLesson, 'result', '')
    const response = _.get(payload, 'data', {})
    const isPass = response.isPass
    if (
      oldResult === E_LEARNING_LEARNER_LOG_RESULT.PASS ||
      (oldResult === E_LEARNING_LEARNER_LOG_RESULT.FAIL && !isPass)
    ) {
      return
    }

    let percent = 100
    let result = E_LEARNING_LEARNER_LOG_RESULT.PASS
    if (!isPass) {
      percent = 0
      result = E_LEARNING_LEARNER_LOG_RESULT.FAIL
      listenerApi.dispatch(resetModuleProgress())
    } else {
      listenerApi.dispatch(setEnableModule())
    }
    listenerApi.dispatch(setModuleProgress(percent))
    listenerApi.dispatch(setSubmitModule(true))
    // overall progress % and handleCheckLessonComplete count module.result —
    // this used to come back from the removed PUT elog/learner-per-lesson
    listenerApi.dispatch(setLessonData({ key: 'result', value: result }))

    if (isPass) {
      listenerApi.dispatch(handleCheckLessonComplete())
    }
  },
})

// handle e-testing time out
listenerMiddlewareELearning.startListening({
  type: 'eTesting/getETesting/rejected',
  effect: async ({ meta }, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    if (_.isEmpty(selectedLesson)) return
    if (selectedLesson.result === E_LEARNING_LEARNER_LOG_RESULT.FAIL) return

    if (_.get(meta.response.data, 'property', '').includes('TIME_OUT')) {
      const body = {
        logLearnerPerLessonUuid: _.get(selectedLesson, 'logUuid', ''),
        data: {
          percent: 0,
          result: E_LEARNING_LEARNER_LOG_RESULT.FAIL,
          startTime: _.get(selectedLesson, 'startTime', ''),
          endTime: null,
          score: null,
          totalScore: null,
        },
      }
      await listenerApi.dispatch(handleUpdateLogLearnerPerLesson(body))
    }
  },
})

// handle update log when submit e-evaluation
listenerMiddlewareELearning.startListening({
  type: 'eEvaluation/postEEvaluationSubmit/fulfilled',
  effect: async (action, listenerApi) => {
    const { selectedLesson } = listenerApi.getState().eLearningLearning
    if (_.isEmpty(selectedLesson)) return

    const isSubmitted = _.get(selectedLesson, 'isSubmitted', '')
    if (isSubmitted) return

    listenerApi.dispatch(setModuleProgress(100))
    listenerApi.dispatch(setEnableModule())
    listenerApi.dispatch(setSubmitModule(true))
    const body = {
      logLearnerPerLessonUuid: _.get(selectedLesson, 'logUuid', ''),
      data: {
        percent: 100,
        result: E_LEARNING_LEARNER_LOG_RESULT.COMPLETE,
        startTime: _.get(selectedLesson, 'startTime', ''),
        endTime: dayjs().format('YYYY-MM-DD HH:mm:ss').toString(),
      },
    }
    await listenerApi.dispatch(handleUpdateLogLearnerPerLesson(body))
  },
})
